import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { RefreshCw, Code2, Layers, Rocket } from 'lucide-react'; // For process steps
import ThemeToggle from './ThemeToggle';
import Navbar from './Navbar';
import Footer from './Footer';

// --- Styled Components ---

const Nav = styled.nav`
  position: fixed;
  top: 0;
  width: 100%;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 5%;
  background: ${props => props.scrolled ? 'var(--nav-bg)' : 'transparent'};
  backdrop-filter: ${props => props.scrolled ? 'blur(12px)' : 'none'};
  border-bottom: ${props => props.scrolled ? '1px solid var(--glass-border)' : 'none'};
  transition: all 0.3s ease;
  z-index: 1000;
`;

const Logo = styled(Link)`
  font-family: 'Outfit', sans-serif;
  font-size: 1.5rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: var(--text-main);
  
  span {
    background: linear-gradient(to right, var(--primary), var(--secondary));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 30px;
  align-items: center;
  
  @media (max-width: 1024px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-muted);
  transition: color 0.3s;
  
  &:hover {
    color: var(--text-main);
  }
`;

const PageContainer = styled.div`
  padding-top: 120px;
  min-height: 100vh;
  background: var(--bg-dark);
  color: var(--text-main);
`;

const ContentSection = styled.section`
  padding: 80px 5%;
  max-width: 1200px;
  margin: 0 auto;
`;

const Title = styled(motion.h1)`
  font-size: clamp(2rem, 5vw, 3rem);
  margin-bottom: 40px;
  text-align: center;
  background: linear-gradient(to right, var(--text-main), var(--text-muted));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const FeatureGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
  margin-top: 40px;
`;

const FeatureCard = styled(motion.div)`
  padding: 40px;
  background: var(--bg-card);
  border: 1px solid var(--glass-border);
  border-radius: 24px;
  transition: all 0.3s;
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  overflow: hidden;
  
  &:hover {
    border-color: var(--primary);
    background: rgba(17, 24, 39, 0.9);
    transform: translateY(-5px);
    box-shadow: 0 10px 40px -10px rgba(99, 102, 241, 0.2);
  }
`;

const IconBox = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 16px;
  background: rgba(99, 102, 241, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary);
  margin-bottom: 24px;
`;

const FeatureList = styled.ul`
  list-style: none;
  margin-top: auto;
  padding-top: 24px;
  border-top: 1px solid var(--glass-border);
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  color: var(--text-muted);
  font-size: 0.95rem;
  
  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--primary);
  }
`;

const ProcessSection = styled.section`
  margin-top: 120px;
  margin-bottom: 80px;
`;

const ProcessGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 60px;
  max-width: 800px;
  margin: 60px auto 0;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    left: 29px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: linear-gradient(180deg, var(--primary) 0%, transparent 100%);
    z-index: 0;
    
    @media (max-width: 768px) {
      left: 20px;
    }
  }
`;

const ProcessStep = styled(motion.div)`
  display: flex;
  align-items: flex-start;
  gap: 40px;
  position: relative;
  
  @media (max-width: 768px) {
    gap: 20px;
  }
`;

const StepIcon = styled.div`
  width: 60px;
  height: 60px;
  min-width: 60px;
  border-radius: 50%;
  background: var(--bg-card);
  border: 2px solid var(--primary);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
  color: var(--text-main);
  font-weight: bold;
  
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    min-width: 40px;
    font-size: 0.8rem;
  }
`;

const LangContainer = styled.div`
  position: relative;
  margin-left: 20px;
`;

const LangButton = styled.button`
  background: transparent;
  border: 1px solid var(--glass-border);
  color: var(--text-main);
  padding: 8px 16px;
  border-radius: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  transition: all 0.3s;
  
  &:hover {
    border-color: var(--primary);
    background: rgba(255, 255, 255, 0.05);
  }
`;

const LangMenu = styled(motion.div)`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 10px;
  background: var(--bg-card);
  border: 1px solid var(--glass-border);
  border-radius: 12px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 120px;
  backdrop-filter: blur(12px);
  overflow: hidden;
`;

const LangItem = styled.button`
  background: transparent;
  border: none;
  color: var(--text-muted);
  padding: 8px 12px;
  text-align: left;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: white;
  }
  
  ${props => props.active && `
    background: rgba(99, 102, 241, 0.1);
    color: var(--primary);
  `}
`;

const DynamicIcon = ({ name }) => {
  const Icon = Icons[name] || Icons.Globe;
  return <Icon size={28} />;
};

const Services = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);

    fetch(`${process.env.REACT_APP_API_URL || ''}/api/services`)
      .then(res => res.json())
      .then(data => {
        setServices(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const processIcons = [<Layers size={24} />, <Code2 size={24} />, <RefreshCw size={24} />, <Rocket size={24} />];

  return (
    <>
      <Navbar />

      <PageContainer>
        <ContentSection>
          <Title
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {t('services.title')}
          </Title>

          <FeatureGrid
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.2
                }
              }
            }}
          >
            {services.map((service, index) => {
              // Simple markdown parsing for lists in description
              const lines = service.description.split('\n');
              const features = lines.filter(line => line.trim().startsWith('-')).map(line => line.replace(/^-\s*/, ''));
              const desc = lines.filter(line => !line.trim().startsWith('-')).join('\n');

              return (
                <FeatureCard
                  key={service.id || index}
                  variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } }}
                >
                  <IconBox><DynamicIcon name={service.icon} /></IconBox>
                  <h3 style={{ marginBottom: '16px', fontSize: '1.5rem' }}>{service.title}</h3>
                  <p style={{ color: 'var(--text-muted)', lineHeight: '1.6', flex: 1, whiteSpace: 'pre-line' }}>{desc}</p>

                  {features.length > 0 && (
                    <FeatureList>
                      {features.map((feature, i) => (
                        <FeatureItem key={i}>{feature}</FeatureItem>
                      ))}
                    </FeatureList>
                  )}
                </FeatureCard>
              );
            })}
            {loading && <div style={{ textAlign: 'center', width: '100%', gridColumn: '1 / -1' }}>Loading services...</div>}
          </FeatureGrid>

          <ProcessSection>
            <Title
              as="h2"
              style={{ fontSize: '2.5rem', marginBottom: '60px' }}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              {t('services.process.title')}
            </Title>
            <ProcessGrid>
              {(t('services.process.steps', { returnObjects: true }) || []).map((step, i) => (
                <ProcessStep
                  key={i}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.2 }}
                  viewport={{ once: true }}
                >
                  <StepIcon>{processIcons[i] || i + 1}</StepIcon>
                  <div>
                    <h4 style={{ marginBottom: '10px', fontSize: '1.3rem' }}>{step.t}</h4>
                    <p style={{ color: 'var(--text-muted)', fontSize: '1rem', lineHeight: '1.6' }}>{step.d}</p>
                  </div>
                </ProcessStep>
              ))}
            </ProcessGrid>
          </ProcessSection>

        </ContentSection>
      </PageContainer>
      <Footer />
    </>
  );
};

export default Services;
